import type { OptionValue } from "@views/Upholstery/types"
import type { FactoryMatchVariation } from "../../factory/factory-match-filter"
import {
  extractFactoryMatchAttributes,
  getSeatAndRowSlices,
} from "../../../pdm/normalizePdm"
import type { PdmFlatRow } from "../../../pdm/normalizePdm"

export type FactoryMatchProductContext = {
  row: PdmFlatRow
  basePart: string
  partNumber: string
  trim: string
  bodyType: string
  seatInfo: ReturnType<typeof getSeatAndRowSlices>
  attrs: ReturnType<typeof extractFactoryMatchAttributes>
}

export type FactoryMatchSlugMaps = {
  trim: Map<string, string>
  bodyType: Map<string, string>
  driverSeat: Map<string, string>
  passengerSeat: Map<string, string>
  frontRow: Map<string, string>
  backRow: Map<string, string>
  middleRow: Map<string, string>
}

export function buildSlugLookup(opts: OptionValue[]): Map<string, string> {
  const map = new Map<string, string>()
  for (const o of opts) {
    const slug = o.slug?.trim()
    if (!slug) continue
    for (const raw of [o.optionName, o.optionValue]) {
      const label = (raw ?? "").trim().toLowerCase()
      if (label) map.set(label, slug)
    }
  }
  return map
}

export function buildFactoryMatchSlugMaps(
  trimOpts: OptionValue[],
  bodyTypeOpts: OptionValue[],
  drvOpts: OptionValue[],
  pasOpts: OptionValue[],
  frontOpts: OptionValue[],
  backOpts: OptionValue[],
  midOpts: OptionValue[]
): FactoryMatchSlugMaps {
  return {
    trim: buildSlugLookup(trimOpts),
    bodyType: buildSlugLookup(bodyTypeOpts),
    driverSeat: buildSlugLookup(drvOpts),
    passengerSeat: buildSlugLookup(pasOpts),
    frontRow: buildSlugLookup(frontOpts),
    backRow: buildSlugLookup(backOpts),
    middleRow: buildSlugLookup(midOpts),
  }
}

export function lookupSlug(
  map: Map<string, string>,
  value: string
): string | undefined {
  const key = value.trim().toLowerCase()
  return key ? map.get(key) : undefined
}

/** Resolve Dato/rows-step display label from filter slug (e.g. option-1 → "60-40"). */
export function findOptionLabelBySlug(
  opts: OptionValue[],
  slug: string
): string {
  const target = slug.trim()
  if (!target) return ""
  const match = opts.find((o) => (o.slug ?? "").trim() === target)
  if (!match) return ""
  return (match.optionName ?? match.optionValue ?? "").trim()
}

/**
 * Row config label for factory match card header + seat diagram subtext.
 * Uses PDM part_attributes when present; otherwise rows-step option labels via filter slug.
 */
export function resolveRowConfigDisplayLabel(
  ctx: FactoryMatchProductContext,
  filterSlug: string,
  backOpts: OptionValue[],
  frontOpts: OptionValue[]
): string {
  const { seatInfo } = ctx
  if (seatInfo.backRow.trim()) return seatInfo.backRow.trim()
  if (seatInfo.middleRow.trim()) return seatInfo.middleRow.trim()
  if (seatInfo.frontRow.trim()) return seatInfo.frontRow.trim()

  return (
    findOptionLabelBySlug(backOpts, filterSlug) ||
    findOptionLabelBySlug(frontOpts, filterSlug) ||
    ""
  )
}

/**
 * Filter slug for a factory match card.
 * Prefer the variation-based slug (trim / middle row / etc.).
 * Only fall back to back/front option index when no tracked dimension varies
 * (PDM omitted row attrs case — align card index to rows-step slugs).
 */
export function resolveFactoryMatchCardFilterSlug(
  factoryMatchCounter: number,
  ctx: FactoryMatchProductContext,
  variation: FactoryMatchVariation,
  slugMaps: FactoryMatchSlugMaps,
  backOpts: OptionValue[],
  frontOpts: OptionValue[]
): string {
  const fallback = `option-${factoryMatchCounter}`
  const fromVariation = resolveFactoryMatchFilterSlug(
    ctx,
    variation,
    slugMaps,
    fallback
  )

  const hasTrackedVariation =
    variation.bodyTypesVary ||
    variation.driverSeatsVary ||
    variation.backRowsVary ||
    variation.passengerSeatsVary ||
    variation.trimsVary ||
    variation.frontRowsVary ||
    variation.middleRowsVary

  if (hasTrackedVariation) {
    return fromVariation
  }

  if (backOpts.length > 0) {
    const backSlug = backOpts[factoryMatchCounter - 1]?.slug?.trim()
    if (backSlug) {
      return backSlug
    }
  }

  if (frontOpts.length > 0) {
    const frontSlug = frontOpts[factoryMatchCounter - 1]?.slug?.trim()
    if (frontSlug) {
      return frontSlug
    }
  }

  return fromVariation
}

/**
 * True when every PDM Middle Row label that drives filtering maps to
 * exactly one `base_part_number`. Uses only PDM fields — no vehicle maps.
 */
export function computeMiddleRowsUniquelyIdentifyKits(
  contexts: FactoryMatchProductContext[],
  drivingMiddleRows: Set<string>
): boolean {
  if (drivingMiddleRows.size < 2) return false

  const middleToBases = new Map<string, Set<string>>()
  for (const ctx of contexts) {
    const middle = ctx.seatInfo.middleRow.trim()
    if (!middle || !drivingMiddleRows.has(middle)) continue
    const base = ctx.basePart.trim()
    if (!base) continue
    const owners = middleToBases.get(middle) ?? new Set<string>()
    owners.add(base)
    middleToBases.set(middle, owners)
  }

  if (middleToBases.size < 2) return false
  return Array.from(middleToBases.values()).every((owners) => owners.size === 1)
}

export function analyzeFactoryMatchVariation(
  contexts: FactoryMatchProductContext[]
): FactoryMatchVariation {
  const styleVariantBases = findStyleVariantBases(contexts)
  const trims = new Set<string>()
  const bodyTypes = new Set<string>()
  const driverSeats = new Set<string>()
  const passengerSeats = new Set<string>()
  const frontRows = new Set<string>()
  const middleRows = new Set<string>()
  const backRows = new Set<string>()

  for (const ctx of contexts) {
    if (ctx.trim) trims.add(ctx.trim)
    if (ctx.bodyType) bodyTypes.add(ctx.bodyType)
    if (ctx.seatInfo.driverSeat) driverSeats.add(ctx.seatInfo.driverSeat)
    if (ctx.seatInfo.passengerSeat)
      passengerSeats.add(ctx.seatInfo.passengerSeat)
    if (ctx.seatInfo.frontRow) frontRows.add(ctx.seatInfo.frontRow)
    addLayoutValueIfItShouldDriveFiltering(
      middleRows,
      ctx,
      ctx.seatInfo.middleRow,
      styleVariantBases,
      (item) => item.seatInfo.middleRow,
      contexts
    )
    addLayoutValueIfItShouldDriveFiltering(
      backRows,
      ctx,
      ctx.seatInfo.backRow,
      styleVariantBases,
      (item) => item.seatInfo.backRow,
      contexts
    )
  }

  const middleRowsVary = middleRows.size > 1

  return {
    bodyTypesVary: bodyTypes.size > 1,
    driverSeatsVary: driverSeats.size > 1,
    backRowsVary: backRows.size > 1,
    passengerSeatsVary: passengerSeats.size > 1,
    trimsVary: trims.size > 1,
    frontRowsVary: frontRows.size > 1,
    middleRowsVary,
    middleRowsUniquelyIdentifyKits: computeMiddleRowsUniquelyIdentifyKits(
      contexts,
      middleRows
    ),
  }
}

/**
 * A base is a style-variant group when PDM lists more than one part number
 * and those parts also differ by color or stitch. Those extra part numbers
 * are color kits, not extra body/row layouts.
 */
function findStyleVariantBases(
  contexts: FactoryMatchProductContext[]
): Set<string> {
  const byBase = new Map<
    string,
    { parts: Set<string>; colors: Set<string>; stitches: Set<string> }
  >()

  for (const ctx of contexts) {
    const bucket = byBase.get(ctx.basePart) ?? {
      parts: new Set<string>(),
      colors: new Set<string>(),
      stitches: new Set<string>(),
    }
    if (ctx.partNumber) bucket.parts.add(ctx.partNumber)
    if (ctx.attrs.primaryColor) bucket.colors.add(ctx.attrs.primaryColor)
    if (ctx.attrs.stitch) bucket.stitches.add(ctx.attrs.stitch)
    byBase.set(ctx.basePart, bucket)
  }

  const out = new Set<string>()
  for (const [base, bucket] of Array.from(byBase.entries())) {
    if (
      bucket.parts.size > 1 &&
      (bucket.colors.size > 1 || bucket.stitches.size > 1)
    ) {
      out.add(base)
    }
  }
  return out
}

function layoutIsUnanimousForBase(
  contexts: FactoryMatchProductContext[],
  basePart: string,
  getLayout: (ctx: FactoryMatchProductContext) => string
): boolean {
  const values = new Set(
    contexts
      .filter((ctx) => ctx.basePart === basePart)
      .map((ctx) => getLayout(ctx).trim())
      .filter(Boolean)
  )
  return values.size <= 1
}

/**
 * Keep PDM layout strings as-is. If one base has several color kits whose
 * back/middle labels do not agree, do not treat that as a real row change
 * for the whole vehicle.
 */
function addLayoutValueIfItShouldDriveFiltering(
  values: Set<string>,
  ctx: FactoryMatchProductContext,
  rawValue: string,
  styleVariantBases: Set<string>,
  getLayout: (ctx: FactoryMatchProductContext) => string,
  contexts: FactoryMatchProductContext[]
): void {
  const layout = rawValue.trim()
  if (!layout) return
  if (
    styleVariantBases.has(ctx.basePart) &&
    !layoutIsUnanimousForBase(contexts, ctx.basePart, getLayout)
  ) {
    return
  }
  values.add(layout)
}

export function buildFactoryMatchDescriptiveName(
  ctx: FactoryMatchProductContext,
  variation: FactoryMatchVariation,
  stitchVariesInGroup: boolean
): string {
  const { trim, bodyType, seatInfo, attrs, basePart } = ctx

  if (variation.bodyTypesVary && bodyType) return bodyType
  if (variation.driverSeatsVary && seatInfo.driverSeat) {
    return `${seatInfo.driverSeat} Driver Seat`
  }
  if (variation.backRowsVary && seatInfo.backRow) return seatInfo.backRow
  if (stitchVariesInGroup && (attrs.primaryColor || attrs.stitch)) {
    return attrs.primaryColor || attrs.stitch
  }
  if (variation.passengerSeatsVary && seatInfo.passengerSeat) {
    return `${seatInfo.passengerSeat} Passenger Seat`
  }
  if (
    variation.middleRowsVary &&
    variation.middleRowsUniquelyIdentifyKits &&
    seatInfo.middleRow
  ) {
    return seatInfo.middleRow
  }
  if (variation.trimsVary && trim) return trim
  if (variation.frontRowsVary && seatInfo.frontRow) return seatInfo.frontRow
  if (variation.middleRowsVary && seatInfo.middleRow) return seatInfo.middleRow

  return (
    trim ||
    bodyType ||
    (seatInfo.driverSeat ? `${seatInfo.driverSeat} Driver Seat` : "") ||
    seatInfo.backRow ||
    attrs.stitch ||
    basePart
  )
}

export function resolveFactoryMatchFilterSlug(
  ctx: FactoryMatchProductContext,
  variation: FactoryMatchVariation,
  slugMaps: FactoryMatchSlugMaps,
  fallbackSlug: string
): string {
  const { trim, bodyType, seatInfo } = ctx

  if (variation.bodyTypesVary && bodyType) {
    return lookupSlug(slugMaps.bodyType, bodyType) ?? fallbackSlug
  }
  if (variation.driverSeatsVary && seatInfo.driverSeat) {
    return lookupSlug(slugMaps.driverSeat, seatInfo.driverSeat) ?? fallbackSlug
  }
  if (variation.backRowsVary && seatInfo.backRow) {
    return lookupSlug(slugMaps.backRow, seatInfo.backRow) ?? fallbackSlug
  }
  if (variation.passengerSeatsVary && seatInfo.passengerSeat) {
    return (
      lookupSlug(slugMaps.passengerSeat, seatInfo.passengerSeat) ?? fallbackSlug
    )
  }
  if (
    variation.middleRowsVary &&
    variation.middleRowsUniquelyIdentifyKits &&
    seatInfo.middleRow
  ) {
    return lookupSlug(slugMaps.middleRow, seatInfo.middleRow) ?? fallbackSlug
  }
  if (variation.trimsVary && trim) {
    return lookupSlug(slugMaps.trim, trim) ?? fallbackSlug
  }
  if (variation.frontRowsVary && seatInfo.frontRow) {
    return lookupSlug(slugMaps.frontRow, seatInfo.frontRow) ?? fallbackSlug
  }
  if (variation.middleRowsVary && seatInfo.middleRow) {
    return lookupSlug(slugMaps.middleRow, seatInfo.middleRow) ?? fallbackSlug
  }

  return (
    lookupSlug(slugMaps.backRow, seatInfo.backRow) ??
    lookupSlug(slugMaps.bodyType, bodyType) ??
    lookupSlug(slugMaps.trim, trim) ??
    fallbackSlug
  )
}
